import { Alert } from '@themenu/design-system'
import { SCAProvider, type SCAProviderProps } from '@themenu/react-sca'
import React, { lazy, Suspense, useEffect, type ReactElement } from 'react'
import { useIntl } from 'react-intl'

import { Loader } from '../components/Loader'
import { ErrorBoundary } from '../components/Shared/ErrorBoundary'
import { Layout } from '../components/Shared/Layout'
import { useUserPhoneNumber } from '../hooks/useUserPhoneNumber'

const LazyProfilePage = lazy(() => import('../pages/ProfilePage').then((module) => ({ default: module.ProfilePage })))
import { updatePlatformHeader } from '../services/api/auth'
import { getAccessToken } from '../services/authentication'
import { setPlatformForLogs } from '../services/datadog'
import { environmentVariables } from '../utils/environmentVariables'
import { redirectToUrl } from '../utils/location'

const SCAProviderComponent = SCAProvider as React.FC<SCAProviderProps>

export const ProfileAndroidRoute = (): ReactElement => {
  const platform = 'android'

  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  const intl = useIntl()
  const locale = intl.locale as 'en-US' | 'fr-FR' | 'pt-BR' | 'es-MX'
  const { phoneNumber, isLoading } = useUserPhoneNumber()

  useEffect(() => {
    if (!getAccessToken()) {
      redirectToUrl(`${environmentVariables('AUTH_API_URL')}/signin`)
    }
  }, [])

  if (!getAccessToken()) {
    return <Loader />
  }

  if (isLoading) {
    return <Loader />
  }

  const profileErrorFallback = (
    <Layout hideFooter>
      <div className="flex h-dvh items-center justify-center px-20">
        <Alert
          backgroundOpacity="solid"
          severity="error"
          title={intl.formatMessage({ id: 'profile.error.title' })}
          secondaryText={intl.formatMessage({ id: 'profile.error.boundary' })}
        />
      </div>
    </Layout>
  )

  return (
    <SCAProviderComponent
      locale={locale}
      phoneNumber={phoneNumber ?? ''}
      scaEnabled={true}
      onStartHelpChat={() => window.open(environmentVariables('HELP_URL'), '_blank')}
    >
      <ErrorBoundary fallback={profileErrorFallback}>
        <Suspense fallback={<Loader />}>
          <LazyProfilePage showBackButton={false} showSettings={false} />
        </Suspense>
      </ErrorBoundary>
    </SCAProviderComponent>
  )
}
